import React, { type FormEvent, useCallback, useState } from 'react';

import { FormattedMessage, useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import Button from '@atlaskit/button/default/button';
import IconButton from '@atlaskit/button/icon/button';
import Heading from '@atlaskit/heading/heading';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import TrashIcon from '@atlaskit/icon/core/delete';
import { Popup } from '@atlaskit/popup/popup';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Flex, Stack, xcss } from '@atlaskit/primitives';
import Radio from '@atlaskit/radio/radio';
import TextField from '@atlaskit/textfield/text-field';
import { token } from '@atlaskit/tokens';

import { ButtonStyle } from '../../../types';
import { LinkPicker, type OnSubmitPayload } from '../../LinkPicker';
import { messages } from './messages';

const popupWrapper = xcss({
	width: '262px',
});

const defaultRadioOptionStyles = xcss({
	paddingInline: 'space.250',
	paddingBlock: 'space.050',
	border: `${token('border.width')} solid ${token('color.border')}`,
	borderRadius: 'radius.small',
	backgroundColor: 'elevation.surface.overlay',
	color: 'color.text',
});

const alternativeRadioOptionStyles = xcss({
	backgroundColor: 'color.background.neutral.bold',
	color: 'color.text.inverse',
});

export type ButtonParams<TStyleOption> = {
	isButtonShown: boolean;
	link: string;
	style: TStyleOption;
	text?: string;
};

export type ButtonActions<TStyleOption> = {
	handleClearButton: () => void;
	handleClearLink: () => void;
	handleSelectLink: (value: OnSubmitPayload) => void;
	updateButtonStyle: (value: ButtonParams<TStyleOption>['style']) => void;
	updateButtonText: (value: ButtonParams<TStyleOption>['text']) => void;
};

type ButtonStyleOption<TStyleOption> = {
	label: string;
	styles: ReturnType<typeof xcss> | ReturnType<typeof xcss>[];
	value: TStyleOption;
};

type ButtonConfigProps<TStyleOption> = Partial<ButtonParams<TStyleOption>> &
	ButtonActions<TStyleOption> & {
		buttonStyles?: ButtonStyleOption<TStyleOption>[];
		cloudId: string;
		source: string;
	};

export const ButtonConfig = <TStyleOption extends string = ButtonStyle>(
	props: ButtonConfigProps<TStyleOption>,
): React.JSX.Element => {
	const [isOpen, setIsOpen] = useState<boolean>(false);

	return (
		<Popup
			shouldRenderToParent
			isOpen={isOpen}
			onClose={() => setIsOpen(false)}
			placement="bottom-start"
			content={() => <PopupContent {...props} />}
			trigger={(triggerProps) => (
				<Box>
					<Button
						{...triggerProps}
						isSelected={isOpen}
						onClick={() => setIsOpen(!isOpen)}
						iconAfter={(iconProps) => <ChevronDownIcon {...iconProps} size="small" />}
					>
						<FormattedMessage {...messages.buttonHeading} />
					</Button>
				</Box>
			)}
		/>
	);
};

type PopupContentProps<TStyleOption> = ButtonConfigProps<TStyleOption>;

const PopupContent = <TStyleOption extends string = ButtonStyle>({
	text,
	link,
	style,
	source,
	buttonStyles,
	handleSelectLink,
	handleClearLink,
	handleClearButton,
	updateButtonText,
	updateButtonStyle,
	cloudId,
}: PopupContentProps<TStyleOption>) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const intl = useIntl();
	const buttonStyleOptions: ButtonStyleOption<TStyleOption>[] = buttonStyles || [
		{
			label: intl.formatMessage(messages.defaultButtonStyleLabel),
			value: ButtonStyle.DEFAULT as TStyleOption,
			styles: defaultRadioOptionStyles,
		},
		{
			label: intl.formatMessage(messages.alternativeButtonStyleLabel),
			value: ButtonStyle.ALTERNATIVE_1 as TStyleOption,
			styles: [defaultRadioOptionStyles, alternativeRadioOptionStyles],
		},
	];

	const fireTextFieldAnalytics = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'focused',
				actionSubject: 'textField',
				actionSubjectId: 'configPanelButtonField',
				source: `${source}ButtonLabel`,
			},
		}).fire();
	}, [createAnalyticsEvent, source]);

	const fireButtonLinkAnalytics = useCallback(
		(isAddingLink: boolean) => {
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					action: isAddingLink ? 'added' : 'removed',
					actionSubject: 'buttonLink',
					actionSubjectId: 'configPanelButtonLink',
					source: `${source}ButtonLink`,
				},
			}).fire();
		},
		[createAnalyticsEvent, source],
	);

	const selectLink = useCallback(
		(selectedLink: OnSubmitPayload) => {
			handleSelectLink(selectedLink);
			fireButtonLinkAnalytics(true);
		},
		[fireButtonLinkAnalytics, handleSelectLink],
	);

	const handleDeleteButton = () => {
		fireButtonLinkAnalytics(false);
		handleClearButton();
	};

	const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const selectedOption = buttonStyleOptions.find(
			(option) => option.value === event.currentTarget.value,
		);
		if (selectedOption) {
			updateButtonStyle(selectedOption.value);
		}
	};

	return (
		<Box padding="space.150" xcss={popupWrapper}>
			<Stack space="space.200" grow="fill">
				<Flex justifyContent="space-between" alignItems="center">
					<Box paddingBlock="space.050">
						<Heading size="xsmall">
							<FormattedMessage {...messages.buttonHeading} />
						</Heading>
					</Box>
					<IconButton
						isDisabled={!(text || link)}
						spacing="compact"
						appearance="subtle"
						onClick={handleDeleteButton}
						icon={TrashIcon}
						label={intl.formatMessage(messages.deleteIconLabel)}
						testId="delete-button"
					/>
				</Flex>
				<Stack testId="link-picker-wrapper">
					<LinkPicker
						cloudId={cloudId}
						link={link}
						id="button-link"
						handleSelectLink={selectLink}
						handleClearLink={handleClearLink}
					/>
				</Stack>
				<Stack>
					<TextField
						maxLength={40}
						value={text}
						onChange={(event: FormEvent<HTMLInputElement>) => {
							updateButtonText(event.currentTarget.value);
						}}
						onFocus={fireTextFieldAnalytics}
						placeholder={intl.formatMessage(messages.textPlaceholder)}
						testId="button-name"
					/>
				</Stack>
				<Flex alignItems="center" justifyContent="space-between">
					{buttonStyleOptions.map((option, index) => (
						<Radio
							key={index}
							value={option.value}
							label={
								<Flex alignItems="center">
									<Flex xcss={option.styles}>
										<FormattedMessage {...messages.buttonHeading} />
									</Flex>
								</Flex>
							}
							name={option.label}
							isChecked={option.value === style}
							onChange={handleChange}
							// Static styling is necessary here to ensure the radio button is centered
							// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
							style={{ margin: 'auto' }}
							testId={`button-style-${option.value}`}
						/>
					))}
				</Flex>
			</Stack>
		</Box>
	);
};
